// Configuración del Turbo automático. Todo lo que Mercado Libre puede cambiar
// (radio, franjas, promesa) se edita acá y no en el código: un turbo que entra
// se despacha con lo que diga esta pantalla en ese momento.
//
// Tres tarjetas: los parámetros del despacho (fila única de turbo_config), las
// franjas horarias de compra (turbo_delivery_windows), que se reemplazan en
// bloque al guardar — lo que no está en la grilla, se borra — y los choferes
// asignados a cada tienda (store_turbo_drivers).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";
import StoreTurboDriversCard from "../components/admin/turbo/StoreTurboDriversCard";

type Config = {
  active: boolean; radius_km: number; promise_hours: number; rings: Ring[]; position_max_age_minutes: number;
  store_driver_radius_km: number; store_offer_timeout_seconds: number | null;
  updated_at: string | null;
};
type Ring = { radius_km: number; ring_size: number; offer_timeout_seconds: number };
type RingRow = { key: string; radius_km: string; ring_size: string; offer_timeout_seconds: string };
const MAX_RINGS = 5;
type WindowRow = { key: string; id?: number; weekday: number; starts_at: string; ends_at: string; deliver_by: string; active: boolean; autoDeliver: boolean };

const WEEKDAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
// Reglas de Mercado Libre para Turbo (doc pública): hasta 7 franjas por día,
// mínimo 3 para operar, solo de lunes a viernes. Se avisan, no se bloquean:
// la regla la aplica ML del lado de ellos.
const ML_MAX_WINDOWS = 7;
const ML_MIN_WINDOWS = 3;

const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: "14px" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-secondary)", fontWeight: 600, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "8px 12px", borderBottom: "1px solid var(--border)", color: "var(--text-primary)", verticalAlign: "middle" };
const panelStyle: React.CSSProperties = { border: "1px solid var(--border)", borderRadius: "18px", background: "rgba(255,255,255,0.03)", padding: "14px" };
const timeStyle: React.CSSProperties = { minHeight: "36px", padding: "0 10px", borderRadius: "12px", border: "1px solid var(--border)", background: "var(--surface-alt)", color: "var(--text-primary)", font: "inherit", width: "150px" };
const WARN = "var(--warning, #d29922)";

const hhmm = (t: string | null | undefined) => (t ? t.slice(0, 5) : "");
const toMin = (t: string) => { const [h, m] = t.split(":").map(Number); return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : NaN; };
const fromMin = (n: number) => { const c = Math.max(0, Math.min(23 * 60 + 59, Math.round(n))); return `${String(Math.floor(c / 60)).padStart(2, "0")}:${String(c % 60).padStart(2, "0")}`; };
// El vencimiento sugerido: inicio de la franja + la promesa (hoy 3 h). ML
// promete "3 horas desde la compra" y operativamente lo resuelve por franja.
// Devuelve "" si el vencimiento cruzaria la medianoche: la franja no se
// podria guardar y no tiene sentido proponerla (ver suggestDeliverBy).
const suggest = (startsAt: string, promiseHours: number) => {
  const m = toMin(startsAt);
  if (!Number.isFinite(m)) return "";
  const d = m + promiseHours * 60;
  return d > 23 * 60 + 59 ? "" : fromMin(d);
};
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
let keySeq = 0;
const newKey = () => `w${Date.now().toString(36)}${(keySeq++).toString(36)}`;

export default function TurboConfigPage() {
  const { token } = useAuth();
  const req = useCallback(async (path: string, init?: RequestInit) => {
    const res = await authFetch(path, { ...(init ?? {}), token, json: init?.body !== undefined });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json?.ok === false) {
      const err: any = new Error(json?.error || `Error ${res.status}`);
      err.errors = json?.errors;
      throw err;
    }
    return json;
  }, [token]);

  const [config, setConfig] = useState<Config | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [rings, setRings] = useState<RingRow[]>([]);
  const [rows, setRows] = useState<WindowRow[]>([]);
  const [dirtyWindows, setDirtyWindows] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorList, setErrorList] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [savingWindows, setSavingWindows] = useState(false);
  const [savingActive, setSavingActive] = useState(false);
  const flash = (m: string) => { setNotice(m); window.setTimeout(() => setNotice(null), 4000); };

  const applyConfig = (c: Config) => {
    setConfig(c);
    setForm({
      radius_km: String(c.radius_km), promise_hours: String(c.promise_hours), position_max_age_minutes: String(c.position_max_age_minutes),
      store_driver_radius_km: String(c.store_driver_radius_km), store_offer_timeout_seconds: c.store_offer_timeout_seconds == null ? "" : String(c.store_offer_timeout_seconds),
    });
    setRings((c.rings ?? []).map((r) => ({ key: newKey(), radius_km: String(r.radius_km), ring_size: String(r.ring_size), offer_timeout_seconds: String(r.offer_timeout_seconds) })));
  };
  const applyWindows = (ws: any[]) => {
    setRows(ws.map((w) => ({ key: newKey(), id: w.id, weekday: Number(w.weekday), starts_at: hhmm(w.starts_at), ends_at: hhmm(w.ends_at), deliver_by: hhmm(w.deliver_by), active: w.active !== false, autoDeliver: false })));
    setDirtyWindows(false);
  };

  const load = useCallback(async () => {
    try {
      const [c, w] = await Promise.all([req("/v2/turbo/config"), req("/v2/turbo/windows")]);
      applyConfig(c.config);
      applyWindows(w.windows ?? []);
      setError(null);
    } catch (e: any) { setError(e.message); }
  }, [req]);
  useEffect(() => { load(); }, [load]);

  const promiseHours = Number(form.promise_hours) || config?.promise_hours || 3;

  const saveConfig = async () => {
    setSavingConfig(true); setError(null); setErrorList([]);
    try {
      // Sin `active`: lo guarda el interruptor (cambiarActivo). Mandar el de
      // la pantalla podía pisar lo que otro admin había cambiado.
      const body: Record<string, unknown> = {};
      for (const k of Object.keys(form)) body[k] = Number(form[k]);
      body.rings = rings.map((r) => ({ radius_km: Number(r.radius_km), ring_size: Number(r.ring_size), offer_timeout_seconds: Number(r.offer_timeout_seconds) }));
      // Vacío = el mismo plazo que el primer anillo.
      body.store_offer_timeout_seconds = form.store_offer_timeout_seconds?.trim() ? Number(form.store_offer_timeout_seconds) : null;
      const r = await req("/v2/turbo/config", { method: "PUT", body: JSON.stringify(body) });
      applyConfig(r.config);
      flash("Parámetros guardados");
    } catch (e: any) { setError(e.message); setErrorList(Array.isArray(e.errors) ? e.errors : []); } finally { setSavingConfig(false); }
  };

  // El interruptor general actúa en el momento: guarda solo `active` (el PUT
  // acepta un cambio parcial) y no toca los parámetros que se estén editando.
  // Antes solo cambiaba la pantalla y quedaba sin guardar hasta «Guardar
  // parámetros»: un apagado de emergencia no apagaba nada.
  const cambiarActivo = async (next: boolean) => {
    const pregunta = next
      ? "¿Encender el despacho automático? Los turbos que entren se van a ofrecer solos a los choferes."
      : "¿Apagar el despacho automático? Los turbos que entren solo van a avisar a los coordinadores.";
    if (!window.confirm(pregunta)) return;
    setSavingActive(true); setError(null); setErrorList([]);
    try {
      const r = await req("/v2/turbo/config", { method: "PUT", body: JSON.stringify({ active: next }) });
      setConfig(r.config);
      flash(r.config.active ? "Despacho automático encendido" : "Despacho automático apagado");
    } catch (e: any) { setError(e.message); setErrorList(Array.isArray(e.errors) ? e.errors : []); } finally { setSavingActive(false); }
  };

  /* ----- anillos de búsqueda ----- */
  const setRing = (key: string, patch: Partial<RingRow>) => setRings((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRing = () => setRings((rs) => {
    const last = rs[rs.length - 1];
    return [...rs, { key: newKey(), radius_km: last ? String(Number(last.radius_km) + 3) : "3", ring_size: last?.ring_size ?? "3", offer_timeout_seconds: last?.offer_timeout_seconds ?? "60" }];
  });
  const removeRing = (key: string) => setRings((rs) => rs.filter((r) => r.key !== key));
  const ringInput = (r: RingRow, k: keyof Omit<RingRow, "key">, step: string) => (
    <input id={`ring-${r.key}-${k}`} type="number" step={step} style={{ ...timeStyle, width: "110px" }} value={r[k]} onChange={(e) => setRing(r.key, { [k]: e.target.value } as Partial<RingRow>)} disabled={!config || savingConfig} />
  );

  /* ----- franjas ----- */
  const byDay = useMemo(() => {
    const m = new Map<number, WindowRow[]>();
    for (const d of WEEKDAY_ORDER) m.set(d, []);
    for (const r of rows) m.get(r.weekday)!.push(r);
    for (const list of m.values()) list.sort((a, b) => (a.starts_at || "99").localeCompare(b.starts_at || "99"));
    return m;
  }, [rows]);

  const setRow = (key: string, patch: Partial<WindowRow>) => {
    setDirtyWindows(true);
    setRows((rs) => rs.map((r) => {
      if (r.key !== key) return r;
      const next = { ...r, ...patch };
      if (patch.starts_at !== undefined) {
        // Al cambiar el inicio se propone fin (+1 h) y vencimiento (+promesa)
        // mientras el operador no los haya tocado a mano.
        const m = toMin(patch.starts_at);
        if (Number.isFinite(m)) {
          if (!r.ends_at || r.autoDeliver) next.ends_at = fromMin(m + 60);
          if (!r.deliver_by || r.autoDeliver) { next.deliver_by = suggest(patch.starts_at, promiseHours); next.autoDeliver = true; }
        }
      }
      if (patch.deliver_by !== undefined) next.autoDeliver = false;
      return next;
    }));
  };
  const addRow = (weekday: number) => {
    setDirtyWindows(true);
    const last = byDay.get(weekday)?.slice(-1)[0];
    const starts = last?.ends_at || "09:00";
    setRows((rs) => [...rs, { key: newKey(), weekday, starts_at: starts, ends_at: fromMin(toMin(starts) + 60), deliver_by: suggest(starts, promiseHours), active: true, autoDeliver: true }]);
  };
  const removeRow = (key: string) => { setDirtyWindows(true); setRows((rs) => rs.filter((r) => r.key !== key)); };
  const copyToWeekdays = (from: number) => {
    // Repite las franjas de un día en los otros de lunes a viernes.
    const src = byDay.get(from) ?? [];
    setDirtyWindows(true);
    setRows((rs) => [
      ...rs.filter((r) => r.weekday === from || r.weekday === 0 || r.weekday === 6),
      ...[1, 2, 3, 4, 5].filter((d) => d !== from).flatMap((d) => src.map((r) => ({ ...r, key: newKey(), id: undefined, weekday: d }))),
    ]);
  };

  const dayWarnings = (weekday: number, list: WindowRow[]) => {
    const w: string[] = [];
    const activos = list.filter((r) => r.active).length;
    if (activos > ML_MAX_WINDOWS) w.push(`Mercado Libre admite hasta ${ML_MAX_WINDOWS} franjas por día (hay ${activos}).`);
    if (activos > 0 && activos < ML_MIN_WINDOWS) w.push(`Mercado Libre pide un mínimo de ${ML_MIN_WINDOWS} franjas activas por día para operar Turbo (hay ${activos}).`);
    if ((weekday === 0 || weekday === 6) && activos > 0) w.push("Mercado Libre no opera Turbo los fines de semana: estas franjas no van a recibir turbos salvo que ML cambie la regla.");
    return w;
  };

  const saveWindows = async () => {
    setSavingWindows(true); setError(null); setErrorList([]);
    try {
      const windows = rows.map((r) => ({ weekday: r.weekday, starts_at: r.starts_at, ends_at: r.ends_at, deliver_by: r.deliver_by, active: r.active }));
      const r = await req("/v2/turbo/windows", { method: "PUT", body: JSON.stringify({ windows }) });
      applyWindows(r.windows ?? []);
      flash(`${windows.length} franjas guardadas`);
    } catch (e: any) { setError(e.message); setErrorList(Array.isArray(e.errors) ? e.errors : []); } finally { setSavingWindows(false); }
  };

  const field = (key: keyof Config, label: string, help: string, step = "1") => (
    <Input id={`turbo-${key}`} label={label} helperText={help} type="number" step={step} value={form[key] ?? ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} disabled={!config || savingConfig} />
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Turbo · Configuración</h2>
            <p style={subtitleStyle}>Las reglas con las que un turbo se convierte solo en un recorrido y se ofrece a los choferes. Lo que Mercado Libre cambie (radio, franjas, promesa) se ajusta acá, sin tocar el sistema.</p>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: "10px", color: "var(--text-primary)", fontWeight: 600 }}>
            <input id="turbo-active" type="checkbox" checked={config?.active ?? false} onChange={(e) => cambiarActivo(e.target.checked)} disabled={!config || savingActive} />
            Despacho automático {config ? (config.active ? "encendido" : "apagado") : ""}
          </label>
        </div>
        <div style={{ ...smallMutedStyle, marginTop: "8px" }}>
          {config ? `Última modificación ${fmtDate(config.updated_at)}` : "Cargando…"}
          {config && !config.active ? <span style={{ color: WARN, marginLeft: "12px" }}>Apagado: los turbos que entren solo avisan a los coordinadores, como antes.</span> : null}
        </div>
      </Card>

      {error ? (
        <div style={{ ...panelStyle, borderColor: "var(--danger)", color: "var(--danger)" }}>
          {error}
          {errorList.length ? <ul style={{ margin: "6px 0 0", paddingLeft: "18px" }}>{errorList.map((x, i) => <li key={i}>{x}</li>)}</ul> : null}
          <div style={{ marginTop: "8px" }}><Button variant="secondary" onClick={() => { setError(null); setErrorList([]); }}>Cerrar</Button></div>
        </div>
      ) : null}
      {notice ? <div style={{ ...panelStyle, borderColor: "var(--success)", color: "var(--success)" }}>{notice}</div> : null}

      <Card>
        <h3 style={{ margin: "0 0 4px", fontSize: "16px", color: "var(--text-primary)" }}>Parámetros del despacho</h3>
        <p style={{ ...smallMutedStyle, margin: "0 0 14px" }}>Radio y promesa vienen de Mercado Libre; los anillos son cómo se busca chofer.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
          {field("radius_km", "Radio tienda → comprador (km)", "Distancia máxima de la tienda al comprador. Más lejos, no se despacha solo.", "0.1")}
          {field("promise_hours", "Promesa de entrega (horas)", "Se usa para sugerir el vencimiento de cada franja: inicio + promesa.", "0.5")}
          {field("position_max_age_minutes", "Posición vigente (minutos)", "Un chofer sin posición más nueva que esto no entra en la oferta.")}
        </div>

        <h4 style={{ margin: "18px 0 4px", fontSize: "14px", color: "var(--text-primary)" }}>Anillos de búsqueda tienda → chofer</h4>
        <p style={{ ...smallMutedStyle, margin: "0 0 10px" }}>
          Se recorren en orden, del más chico al más grande: en cada anillo se ofrece a los choferes más cercanos a la tienda dentro de su radio.
          Si nadie contesta a tiempo, pasa al siguiente; si un anillo no tiene a nadie adentro, se saltea en el momento. Agotados todos, se avisa a los coordinadores.
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr><th style={thStyle}>Anillo</th><th style={thStyle}>Radio (km)</th><th style={thStyle}>Choferes a la vez</th><th style={thStyle}>Segundos para responder</th><th style={thStyle}></th></tr></thead>
            <tbody>
              {rings.map((r, i) => (
                <tr key={r.key}>
                  <td style={tdStyle}><b>{i + 1}</b></td>
                  <td style={tdStyle}>{ringInput(r, "radius_km", "0.1")}</td>
                  <td style={tdStyle}>{ringInput(r, "ring_size", "1")}</td>
                  <td style={tdStyle}>{ringInput(r, "offer_timeout_seconds", "1")}</td>
                  <td style={tdStyle}><Button variant="ghost" onClick={() => removeRing(r.key)} disabled={rings.length <= 1 || savingConfig}>Quitar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: "8px" }}><Button variant="secondary" onClick={addRing} disabled={rings.length >= MAX_RINGS || !config || savingConfig}>Agregar anillo</Button></div>
        <h4 style={{ margin: "18px 0 4px", fontSize: "14px", color: "var(--text-primary)" }}>Choferes asignados a la tienda</h4>
        <p style={{ ...smallMutedStyle, margin: "0 0 10px" }}>Antes de los anillos, el turbo se le ofrece a cada chofer asignado a la tienda, de a uno y en su orden, aunque haya otros más cerca. Si no contesta o no puede, sigue el siguiente; agotados, sigue la regla normal.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "14px" }}>
          {field("store_driver_radius_km", "Radio del asignado (km)", "Distancia máxima chofer → tienda para ofrecérselo. Más lejos, se lo saltea.", "0.1")}
          <Input id="turbo-store_offer_timeout_seconds" label="Segundos para responder del asignado" helperText={`Vacío = lo mismo que el anillo 1 (${rings[0]?.offer_timeout_seconds || "—"} s).`} type="number" step="1" placeholder={rings[0]?.offer_timeout_seconds || ""} value={form.store_offer_timeout_seconds ?? ""} onChange={(e) => setForm((f) => ({ ...f, store_offer_timeout_seconds: e.target.value }))} disabled={!config || savingConfig} />
        </div>
        <div style={{ display: "flex", gap: "10px", marginTop: "14px", flexWrap: "wrap" }}>
          <Button onClick={saveConfig} disabled={!config || savingConfig}>{savingConfig ? "Guardando…" : "Guardar parámetros"}</Button>
          <Button variant="secondary" onClick={load} disabled={savingConfig}>Descartar cambios</Button>
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "flex-start", marginBottom: "10px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "16px", color: "var(--text-primary)" }}>Franjas horarias de compra</h3>
            <p style={{ ...smallMutedStyle, margin: "4px 0 0" }}>
              Un turbo comprado dentro de una franja vence a la hora de entrega de esa franja, sin importar el minuto exacto de compra. Al cargar el inicio se propone el vencimiento (inicio + {promiseHours} h); se puede corregir a mano.
              Fuera de todas las franjas no hay despacho automático. Mercado Libre: de lunes a viernes, entre {ML_MIN_WINDOWS} y {ML_MAX_WINDOWS} franjas por día.
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <Button onClick={saveWindows} disabled={savingWindows || !dirtyWindows}>{savingWindows ? "Guardando…" : "Guardar franjas"}</Button>
            <Button variant="secondary" onClick={load} disabled={savingWindows || !dirtyWindows}>Descartar</Button>
          </div>
        </div>

        <div style={{ display: "grid", gap: "12px" }}>
          {WEEKDAY_ORDER.map((weekday) => {
            const list = byDay.get(weekday) ?? [];
            const warnings = dayWarnings(weekday, list);
            return (
              <div key={weekday} style={panelStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <b style={{ color: "var(--text-primary)" }}>{WEEKDAYS[weekday]} <span style={{ ...smallMutedStyle, fontWeight: 400 }}>· {list.filter((r) => r.active).length} activas</span></b>
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {weekday >= 1 && weekday <= 5 && list.length ? <Button variant="ghost" onClick={() => copyToWeekdays(weekday)}>Copiar a lunes–viernes</Button> : null}
                    <Button variant="secondary" onClick={() => addRow(weekday)}>Agregar franja</Button>
                  </div>
                </div>
                {list.length ? (
                  <div style={{ overflowX: "auto", marginTop: "8px" }}>
                    <table style={tableStyle}>
                      <thead><tr><th style={thStyle}>Compra desde</th><th style={thStyle}>Compra hasta</th><th style={thStyle}>Entregar antes de</th><th style={thStyle}>Activa</th><th style={thStyle}></th></tr></thead>
                      <tbody>
                        {list.map((r) => (
                          <tr key={r.key} style={r.active ? undefined : { opacity: 0.55 }}>
                            <td style={tdStyle}><input id={`w-${r.key}-starts`} type="time" style={timeStyle} value={r.starts_at} onChange={(e) => setRow(r.key, { starts_at: e.target.value })} /></td>
                            <td style={tdStyle}><input id={`w-${r.key}-ends`} type="time" style={timeStyle} value={r.ends_at} onChange={(e) => setRow(r.key, { ends_at: e.target.value })} /></td>
                            <td style={tdStyle}>
                              <input id={`w-${r.key}-deliver`} type="time" style={timeStyle} value={r.deliver_by} onChange={(e) => setRow(r.key, { deliver_by: e.target.value })} />
                              {r.autoDeliver ? <span style={{ ...smallMutedStyle, marginLeft: "8px" }}>sugerido</span> : null}
                            </td>
                            <td style={tdStyle}><input id={`w-${r.key}-active`} type="checkbox" checked={r.active} onChange={(e) => setRow(r.key, { active: e.target.checked })} /></td>
                            <td style={tdStyle}><Button variant="ghost" onClick={() => removeRow(r.key)}>Quitar</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div style={{ ...smallMutedStyle, marginTop: "6px" }}>Sin franjas: los turbos de este día no se despachan solos.</div>}
                {warnings.map((w, i) => <div key={i} style={{ ...smallMutedStyle, color: WARN, marginTop: "6px" }}>{w}</div>)}
              </div>
            );
          })}
        </div>
      </Card>

      <StoreTurboDriversCard req={req} />
    </div>
  );
}
